import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Flag, ShieldCheck } from "lucide-react";

const REASONS = ["spam","offensive","harassment","misleading","bad_chat","misuse","false_reports","other"];

export function ReportUserButton({
  reportedUserId,
  reportedUserName,
  contextType,      // "profile" | "report" | "message" | "chat"
  contextRef,       // report_id | message_id | chat_id
  variant = "ghost",
  size = "sm",
  className = "",
  label,
  testid,
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        data-testid={testid || `report-user-btn-${reportedUserId}`}
        variant={variant} size={size} className={`rounded-full text-xs hover:bg-red-50 hover:text-red-800 ${className}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
      >
        <Flag size={12} className="mr-1" /> {label || "Segnala"}
      </Button>
      {open && (
        <ReportUserDialog
          open={open} onOpenChange={setOpen}
          reportedUserId={reportedUserId}
          reportedUserName={reportedUserName}
          contextType={contextType}
          contextRef={contextRef}
        />
      )}
    </>
  );
}

export function ReportUserDialog({ open, onOpenChange, reportedUserId, reportedUserName, contextType, contextRef }) {
  const { t } = useI18n();
  const [reason, setReason] = useState("spam");
  const [explanation, setExplanation] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason) return;
    setBusy(true);
    try {
      await api.post("/user-reports", {
        reported_user_id: reportedUserId,
        reason,
        explanation: explanation || null,
        context_type: contextType || null,
        context_ref: contextRef || null,
      });
      toast.success(t("report_user_sent"));
      onOpenChange(false);
      setExplanation("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-user-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flag size={16} className="text-red-600" /> {t("report_user_title")}</DialogTitle>
        </DialogHeader>
        {reportedUserName && (
          <div className="text-sm text-slate-700"><b>{reportedUserName}</b></div>
        )}
        <div className="mt-2 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">{t("report_user_reason")}</div>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="ur-reason-select" className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`ur-reason-${r}`}>{t(`ur_reason_${r}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">{t("report_user_explanation")}</div>
            <Textarea data-testid="ur-explanation" rows={4} value={explanation} onChange={(e) => setExplanation(e.target.value.slice(0, 2000))} className="rounded-xl text-sm" />
          </div>
          <div className="flex items-start gap-2 text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <ShieldCheck size={14} className="shrink-0 mt-0.5 text-emerald-700" />
            <span>{t("report_user_privacy")}</span>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full hover:bg-slate-100">{t("cancel")}</Button>
          <Button data-testid="ur-submit" onClick={submit} disabled={busy || !reason} className="rounded-full bg-red-600 hover:bg-red-700 text-white">{t("report_user_send")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReportUserButton;
